import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mic, MicOff, Video, VideoOff, Camera, Wand2 } from "lucide-react";
import { toast } from "sonner";
import AgoraRTC from "agora-rtc-sdk-ng";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatInTz, userBrowserTimezone } from "@/lib/time";
import { getDevicePrefs, setDevicePrefs, setJoinPrefs, getVideoEffects, setVideoEffects } from "@/lib/devicePrefs";
import { LOBBY_AGORA_PREVIEW_ENABLED } from "@/lib/featureFlags";
import { checkVideoEffectsCompatibility, createEffects, applyEffects, disposeEffects, loadBackgroundImage } from "@/lib/videoEffects";
import VideoEffectsPanel from "@/components/VideoEffectsPanel";
import BloqLogo from "@/components/BloqLogo";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FLAG = LOBBY_AGORA_PREVIEW_ENABLED;

// Lobby-specific test-ids for the shared effects panel (Meeting Room keeps its
// own defaults — do not collide).
const LOBBY_EFFECT_IDS = {
  panel: "lobby-video-effects-panel",
  close: "lobby-video-effects-close",
  bgNone: "lobby-effect-background-off",
  bgBlur: "lobby-effect-background-blur",
  bgImage: "lobby-effect-background-image",
  blurLow: "lobby-effect-blur-low",
  blurMedium: "lobby-effect-blur-medium",
  blurHigh: "lobby-effect-blur-high",
  presets: "lobby-effect-bg-presets",
  presetPrefix: "lobby-effect-preset-",
  upload: "lobby-effect-bg-upload",
  fileInput: "lobby-effect-bg-file-input",
  beauty: "lobby-effect-beauty-toggle",
  sliders: "lobby-effect-beauty-sliders",
  smoothness: "lobby-effect-smoothness",
  brightness: "lobby-effect-brightness",
  redness: "lobby-effect-redness",
};

/**
 * Lobby — preview camera, mic, name BEFORE joining.
 * Works for authenticated users AND guests (if guest_access_enabled).
 *
 * The Lobby owns a DISPOSABLE preview session. When the Agora preview flag is
 * on it creates and fully owns its own Agora camera track + effect processors,
 * disposes them on join/leave, and carries only the user's serializable effect
 * settings into the Meeting Room (which then creates its OWN track). No live
 * track/processor/stream is ever shared with the Meeting Room.
 */
export default function Lobby() {
  const { publicId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [meeting, setMeeting] = useState(null);
  const [error, setError] = useState(null);
  const [errorKind, setErrorKind] = useState(null);  // "not_found" | "forbidden" | "transient" | "unknown"
  const [loading, setLoading] = useState(true);
  const [retryNonce, setRetryNonce] = useState(0);
  const [displayName, setDisplayName] = useState("");
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [cams, setCams] = useState([]);
  const [mics, setMics] = useState([]);
  const [camId, setCamId] = useState("");
  const [micId, setMicId] = useState("");
  const [camOn, setCamOn] = useState(true);
  const [micOn, setMicOn] = useState(true);
  const [permError, setPermError] = useState(null);

  // Legacy raw-getUserMedia preview (flag OFF)
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  // Agora preview session (flag ON) — Lobby owns these exclusively.
  const lobbyPreviewRef = useRef(null);
  const lobbyTrackRef = useRef(null);
  const lobbyEffectsRef = useRef(null);
  const bgImageElRef = useRef(null);

  // Lifecycle guards against remounts / stale async initialisation.
  const isMountedRef = useRef(false);
  const initIdRef = useRef(0);
  const joiningRef = useRef(false);

  // Video effects state
  const [caps] = useState(() =>
    FLAG ? checkVideoEffectsCompatibility() : { backgroundSupported: false, beautySupported: false }
  );
  const effectsSupported = caps.backgroundSupported || caps.beautySupported;
  const [videoEffects, setVideoEffectsState] = useState(() => getVideoEffects());
  const [effectsOpen, setEffectsOpen] = useState(false);
  const [isJoining, setIsJoining] = useState(false);

  useEffect(() => {
    setDisplayName(user?.name || "");
  }, [user]);

  useEffect(() => {
    let cancel = false;
    let attempts = 0;
    setLoading(true);
    setError(null);
    setErrorKind(null);

    // The lobby's previous catch handler collapsed every kind of API
    // failure (404, 5xx, network blip, timeout) into a single misleading
    // "Meeting not found → may be expired" message. This caused
    // intermittent false-negatives during active meetings whenever the
    // backend hiccuped (worker restart, ingress 502, brief network
    // drop). The fix:
    //   1. Classify error by HTTP status / response presence.
    //   2. Silently retry transient errors (5xx / no response) once
    //      with a short backoff so the user never sees the flash.
    //   3. Show an accurate error message + "Try again" affordance
    //      when the error genuinely persists.
    const tryFetch = async () => {
      try {
        const r = await api.get(`/meetings/public/${publicId}`);
        if (cancel) return;
        const data = r.data;
        const status = (data?.status || "").toLowerCase();
        if (
          !data?.is_persistent &&
          ["ended", "completed", "canceled", "cancelled"].includes(status)
        ) {
          navigate(`/m/${publicId}/unavailable`, { replace: true });
          return;
        }
        setMeeting(data);
        setError(null);
        setErrorKind(null);
        setLoading(false);
      } catch (e) {
        if (cancel) return;
        attempts += 1;
        const httpStatus = e?.response?.status;
        const isTransient = !e?.response || (httpStatus >= 500 && httpStatus < 600);

        // Single silent retry — covers the typical FastAPI cold-start,
        // brief Mongo reconnect, or ingress hiccup (all sub-second).
        if (isTransient && attempts < 2) {
          setTimeout(tryFetch, 1500);
          return;
        }

        let msg;
        let kind;
        if (httpStatus === 404) {
          msg = e.response?.data?.detail || "Meeting not found";
          kind = "not_found";
        } else if (httpStatus === 403) {
          msg = e.response?.data?.detail || "You don’t have access to this meeting";
          kind = "forbidden";
        } else if (isTransient) {
          msg = "We couldn’t reach Bloq right now. Please try again in a moment.";
          kind = "transient";
        } else {
          msg = e.response?.data?.detail || "Something went wrong loading this meeting";
          kind = "unknown";
        }
        setError(msg);
        setErrorKind(kind);
        setLoading(false);
      }
    };

    tryFetch();
    return () => { cancel = true; };
  }, [publicId, navigate, retryNonce]);

  // ── Effects helpers ──────────────────────────────────────────────────────

  // Idempotent cleanup of the Lobby preview (legacy stream AND/OR Agora track
  // + processors). Safe to call multiple times.
  const cleanupLobbyVideo = useCallback(async () => {
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch {}
      streamRef.current = null;
    }
    const track = lobbyTrackRef.current;
    const handles = lobbyEffectsRef.current;
    lobbyTrackRef.current = null;
    lobbyEffectsRef.current = null;
    bgImageElRef.current = null;
    try { track?.stop?.(); } catch {}
    if (handles) {
      try { await disposeEffects(handles); } catch (e) { console.warn("Lobby effects cleanup failed", e); }
    }
    try { track?.close?.(); } catch (e) { console.warn("Lobby camera cleanup failed", e); }
  }, []);

  // Apply the given settings to the Lobby's own camera track. Lazily creates
  // processors on first use; never touches the Meeting Room.
  const applyLobbyEffects = useCallback(async (settings) => {
    if (!FLAG || !effectsSupported) return;
    const track = lobbyTrackRef.current;
    if (!track) return;
    const anyOn =
      settings.background === "blur" ||
      (settings.background === "image" && !!settings.imageDataUrl) ||
      settings.beautyEnabled;
    if (!lobbyEffectsRef.current) {
      if (!anyOn) return;
      lobbyEffectsRef.current = await createEffects(track, {
        onOverload: (which) => {
          // Disable ONLY the affected effect; keep the camera + the other one.
          const patch = which === "beauty" ? { beautyEnabled: false } : { background: "none" };
          const next = { ...getVideoEffects(), ...patch };
          setVideoEffectsState(next);
          setVideoEffects(next);
          if (lobbyEffectsRef.current) {
            applyEffects(lobbyEffectsRef.current, { ...next, imageEl: bgImageElRef.current }).catch(() => {});
          }
          toast.message("A video effect was turned off to keep your camera smooth.");
        },
      });
    }
    let imageEl = null;
    // Only load when an actual image URL exists. Switching to "image" mode
    // before picking a preset/upload is a benign intermediate state — keep the
    // background as "image" so the panel's preset gallery stays visible.
    if (settings.background === "image" && settings.imageDataUrl) {
      try {
        imageEl = bgImageElRef.current || (await loadBackgroundImage(settings.imageDataUrl));
        bgImageElRef.current = imageEl;
      } catch {
        toast.error("Background image couldn't load — turning background off.");
        setVideoEffectsState((prev) => ({ ...prev, background: "none" }));
        setVideoEffects({ background: "none" });
        await applyEffects(lobbyEffectsRef.current, { ...settings, background: "none" });
        return;
      }
    }
    await applyEffects(lobbyEffectsRef.current, { ...settings, imageEl });
  }, [effectsSupported]);

  // Effects-panel change handler — persist + apply live.
  const updateVideoEffects = useCallback(async (patch) => {
    if (patch.imageDataUrl !== undefined) bgImageElRef.current = null;
    const next = { ...videoEffects, ...patch };
    setVideoEffectsState(next);
    setVideoEffects(next);
    try { await applyLobbyEffects(next); }
    catch (e) { console.warn("lobby effects apply failed", e); toast.error("Couldn't apply that video effect."); }
  }, [videoEffects, applyLobbyEffects]);

  // Recreate a fresh preview track after a failed/canceled join.
  const restoreLobbyPreviewSafely = useCallback(async () => {
    if (!FLAG || !isMountedRef.current) return;
    try {
      const track = await AgoraRTC.createCameraVideoTrack(camId ? { cameraId: camId } : {});
      lobbyTrackRef.current = track;
      try { await track.setEnabled(camOn); } catch {}
      if (effectsSupported) {
        const s = getVideoEffects();
        if ((s.background && s.background !== "none") || s.beautyEnabled) {
          await applyLobbyEffects(s);
        }
      }
      if (lobbyPreviewRef.current && camOn) { try { track.play(lobbyPreviewRef.current); } catch {} }
    } catch (e) {
      console.error("Unable to restore lobby preview", e);
      setPermError("Your camera preview could not be restored, but you may still join with your camera off.");
    }
  }, [camId, camOn, effectsSupported, applyLobbyEffects]);

  // ── Media acquisition ────────────────────────────────────────────────────
  useEffect(() => {
    if (loading || error || !meeting) return;
    if (meeting.status === "canceled" || meeting.status === "ended") return;

    // ---- Legacy raw getUserMedia preview (flag OFF) ----
    if (!FLAG) {
      let active = true;
      (async () => {
        try {
          const saved = getDevicePrefs();
          const constraints = {
            video: saved.camId ? { deviceId: { ideal: saved.camId } } : true,
            audio: saved.micId ? { deviceId: { ideal: saved.micId } } : true,
          };
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          if (!active) { stream.getTracks().forEach((t) => t.stop()); return; }
          streamRef.current = stream;
          if (videoRef.current) videoRef.current.srcObject = stream;
          const all = await navigator.mediaDevices.enumerateDevices();
          const allCams = all.filter((d) => d.kind === "videoinput");
          const allMics = all.filter((d) => d.kind === "audioinput");
          setCams(allCams);
          setMics(allMics);
          const camDeviceId = stream.getVideoTracks()[0]?.getSettings?.()?.deviceId || allCams[0]?.deviceId || "";
          const micDeviceId = stream.getAudioTracks()[0]?.getSettings?.()?.deviceId || allMics[0]?.deviceId || "";
          if (camDeviceId) setCamId(camDeviceId);
          if (micDeviceId) setMicId(micDeviceId);
          setDevicePrefs({ camId: camDeviceId, micId: micDeviceId });
        } catch (e) {
          setPermError("Bloq needs access to your camera and microphone to join the meeting. Check the browser permission icon in the address bar.");
        }
      })();
      return () => {
        active = false;
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => t.stop());
          streamRef.current = null;
        }
      };
    }

    // ---- Agora preview session (flag ON) ----
    isMountedRef.current = true;
    const myInit = ++initIdRef.current;
    (async () => {
      // 1) Permission + device enumeration via an EPHEMERAL stream that we
      //    immediately release so Agora can own the camera device.
      const saved = getDevicePrefs();
      let permStream = null;
      try {
        permStream = await navigator.mediaDevices.getUserMedia({
          video: saved.camId ? { deviceId: { ideal: saved.camId } } : true,
          audio: saved.micId ? { deviceId: { ideal: saved.micId } } : true,
        });
      } catch (e) {
        if (isMountedRef.current && myInit === initIdRef.current) {
          setPermError("Bloq needs access to your camera and microphone to join the meeting. Check the browser permission icon in the address bar.");
        }
        return;
      }
      let allCams = [];
      let allMics = [];
      let camDeviceId = "";
      let micDeviceId = "";
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        allCams = all.filter((d) => d.kind === "videoinput");
        allMics = all.filter((d) => d.kind === "audioinput");
        camDeviceId = permStream.getVideoTracks()[0]?.getSettings?.()?.deviceId || allCams[0]?.deviceId || "";
        micDeviceId = permStream.getAudioTracks()[0]?.getSettings?.()?.deviceId || allMics[0]?.deviceId || "";
      } finally {
        // Release the camera/mic so the Agora track can acquire the device.
        try { permStream.getTracks().forEach((t) => t.stop()); } catch {}
      }

      if (!isMountedRef.current || myInit !== initIdRef.current) return;
      setCams(allCams);
      setMics(allMics);
      if (camDeviceId) setCamId(camDeviceId);
      if (micDeviceId) setMicId(micDeviceId);
      setDevicePrefs({ camId: camDeviceId, micId: micDeviceId });

      // 2) Create the Lobby's own Agora camera track for preview.
      let track;
      try {
        track = await AgoraRTC.createCameraVideoTrack(camDeviceId ? { cameraId: camDeviceId } : {});
      } catch (e) {
        if (isMountedRef.current && myInit === initIdRef.current) {
          setPermError("We couldn't start your camera preview. You can still join with your camera off.");
        }
        return;
      }
      // Stale-init guard: a newer init (or unmount) happened while we awaited.
      if (!isMountedRef.current || myInit !== initIdRef.current) {
        try { track.stop(); track.close(); } catch {}
        return;
      }
      lobbyTrackRef.current = track;
      try { await track.setEnabled(camOn); } catch {}
      if (lobbyPreviewRef.current && camOn) { try { track.play(lobbyPreviewRef.current); } catch {} }

      // 3) Apply the user's saved effect (if any) to the preview.
      if (effectsSupported) {
        try {
          const s = getVideoEffects();
          if ((s.background && s.background !== "none") || s.beautyEnabled) {
            await applyLobbyEffects(s);
          }
        } catch (e) {
          console.warn("lobby effects init failed", e);
        }
      }
    })();

    return () => {
      isMountedRef.current = false;
      initIdRef.current += 1;
      cleanupLobbyVideo();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, meeting]);

  // Camera/mic on-off toggles.
  useEffect(() => {
    if (!FLAG) {
      if (!streamRef.current) return;
      streamRef.current.getVideoTracks().forEach((t) => (t.enabled = camOn));
      streamRef.current.getAudioTracks().forEach((t) => (t.enabled = micOn));
      return;
    }
    // Agora path: toggle the camera track; the mic is UI-only in the lobby
    // (no live audio stream / meter) and is carried into the room via prefs.
    const track = lobbyTrackRef.current;
    if (!track) return;
    (async () => {
      try {
        await track.setEnabled(camOn);
        if (camOn && lobbyPreviewRef.current) { try { track.play(lobbyPreviewRef.current); } catch {} }
      } catch {}
    })();
  }, [camOn, micOn]);

  const switchCam = async (id) => {
    setCamId(id);
    setDevicePrefs({ camId: id });
    if (!FLAG) {
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: id } },
          audio: micId ? { deviceId: { exact: micId } } : true,
        });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = newStream;
        if (videoRef.current) videoRef.current.srcObject = newStream;
        streamRef.current.getVideoTracks().forEach((t) => (t.enabled = camOn));
        streamRef.current.getAudioTracks().forEach((t) => (t.enabled = micOn));
      } catch {
        toast.error("Could not switch camera");
      }
      return;
    }
    // Agora path: switch device in place (keeps the effects pipeline intact).
    const track = lobbyTrackRef.current;
    if (!track) return;
    try { await track.setDevice(id); }
    catch { toast.error("Could not switch camera"); }
  };

  const switchMic = async (id) => {
    setMicId(id);
    setDevicePrefs({ micId: id });
    if (!FLAG) {
      try {
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: camId ? { deviceId: { exact: camId } } : true,
          audio: { deviceId: { exact: id } },
        });
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = newStream;
        if (videoRef.current) videoRef.current.srcObject = newStream;
        streamRef.current.getVideoTracks().forEach((t) => (t.enabled = camOn));
        streamRef.current.getAudioTracks().forEach((t) => (t.enabled = micOn));
      } catch {
        toast.error("Could not switch microphone");
      }
      return;
    }
    // Agora path: no live mic stream in the lobby — selection is persisted and
    // applied by the Meeting Room. Nothing else to do.
  };

  const handleJoin = async () => {
    const name = (displayName || "Guest").trim();
    if (!name) {
      toast.error("Please enter your name");
      return;
    }
    if (joiningRef.current) return;
    joiningRef.current = true;
    setIsJoining(true);
    try {
      // Persist all preferences (effects were already saved live; re-save to
      // be safe). The Meeting Room reads these and creates its own track.
      setVideoEffects(videoEffects);
      sessionStorage.setItem(`bloq_join_prefs:${publicId}`, JSON.stringify({ displayName: name, camOn, micOn, camId, micId, completedAt: Date.now() }));
      sessionStorage.setItem("bloq_join_prefs", JSON.stringify({ displayName: name, camOn, micOn, camId, micId }));
      setJoinPrefs({ displayName: name, camOn, micOn }, publicId);
      setDevicePrefs({ camId, micId });
      // Fully release the Lobby camera + processors BEFORE the Meeting Room
      // creates its own track (never two owners of the device).
      await cleanupLobbyVideo();
      navigate(`/m/${publicId}/room`);
    } catch (e) {
      joiningRef.current = false;
      setIsJoining(false);
      await restoreLobbyPreviewSafely();
      toast.error("We couldn't enter the meeting. Your camera preview has been restored.");
    }
  };

  // Commit the meeting rename. Backend is the source of truth — after PATCH
  // we always re-fetch via /meetings/public/{publicId} so a refreshed lobby
  // never shows the stale title.
  const commitTitle = async () => {
    const t = titleDraft.trim();
    if (!t || t === meeting.title) { setEditingTitle(false); return; }
    setSavingTitle(true);
    try {
      await api.patch(`/meetings/${meeting.meeting_id}`, { title: t });
      const fresh = await api.get(`/meetings/public/${publicId}`);
      setMeeting(fresh.data);
      toast.success("Meeting renamed");
      setEditingTitle(false);
    } catch {
      toast.error("Could not rename");
    } finally {
      setSavingTitle(false);
    }
  };

  if (loading) {
    return <CenteredLoader />;
  }

  if (error) {
    // Different error kinds need different copy + affordances. Transient
    // errors (5xx / network) get a "Try again" button that retriggers
    // the fetch. Real 404/403s keep the existing "Back to Bloq" button
    // because retrying a genuine miss is pointless.
    const isRetryable = errorKind === "transient" || errorKind === "unknown";
    const title =
      errorKind === "not_found"  ? "Meeting unavailable" :
      errorKind === "forbidden"  ? "Access required" :
      errorKind === "transient"  ? "Connection problem" :
                                   "Couldn’t open the meeting";
    const subtext =
      errorKind === "not_found"  ? "The link may be invalid, expired, or the meeting was canceled." :
      errorKind === "forbidden"  ? "Sign in with the account that was invited, or contact the host." :
      errorKind === "transient"  ? "This is usually a brief blip — try again." :
                                   "If this keeps happening, please contact the host.";
    return (
      <CenteredCard title={title}>
        <p className="text-[#4a4a52]" data-testid="lobby-error-message">{error}. {subtext}</p>
        <div className="flex flex-wrap items-center gap-3 mt-5">
          {isRetryable && (
            <button
              type="button"
              className="bloq-btn-primary inline-flex items-center gap-2"
              onClick={() => setRetryNonce((n) => n + 1)}
              data-testid="lobby-error-retry-btn"
            >
              Try again
            </button>
          )}
          <Link
            to="/"
            className={(isRetryable ? "bloq-btn-secondary" : "bloq-btn-primary") + " inline-flex items-center gap-2"}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Bloq
          </Link>
        </div>
      </CenteredCard>
    );
  }
  if (meeting.status === "canceled") {
    return <CenteredCard title="Meeting canceled"><p className="text-[#4a4a52]">This meeting was canceled by the host.</p></CenteredCard>;
  }
  if (meeting.status === "ended") {
    return <CenteredCard title="Meeting ended"><p className="text-[#4a4a52]">This meeting has already ended.</p></CenteredCard>;
  }
  if (!user && !meeting.guest_access_enabled) {
    return (
      <CenteredCard title="This meeting is invite-only">
        <p className="text-[#4a4a52]">Please sign in with the account that was invited, or contact the host.</p>
        <Link to="/login" className="bloq-btn-primary inline-flex items-center gap-2 mt-5 self-start">Sign in</Link>
      </CenteredCard>
    );
  }

  return (
    <div className="min-h-screen bg-[#fbfcfc] flex flex-col">
      <div className="bloq-container pt-6">
        <BloqLogo size={24} />
      </div>
      <div className="flex-1 bloq-container py-10 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <p className="bloq-eyebrow">Lobby</p>
          {editingTitle ? (
            <div className="flex items-center gap-2 mt-2 mb-2" data-testid="lobby-rename-row">
              <input
                className="bloq-input text-3xl font-semibold py-2 flex-1"
                value={titleDraft}
                autoFocus
                onChange={(e) => setTitleDraft(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === "Escape") { setEditingTitle(false); setTitleDraft(meeting.title); }
                  if (e.key === "Enter") {
                    await commitTitle();
                  }
                }}
                data-testid="lobby-rename-input"
                disabled={savingTitle}
              />
              <button
                onClick={commitTitle}
                className="bloq-btn-primary text-xs"
                disabled={savingTitle || !titleDraft.trim() || titleDraft.trim() === meeting.title}
                data-testid="lobby-rename-save"
              >
                {savingTitle ? "Saving…" : "Save"}
              </button>
              <button
                onClick={() => { setEditingTitle(false); setTitleDraft(meeting.title); }}
                className="bloq-btn-secondary text-xs"
                disabled={savingTitle}
                data-testid="lobby-rename-cancel"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 mt-2 mb-2">
              <h1 className="bloq-headline text-4xl sm:text-5xl" data-testid="lobby-meeting-title">{meeting.title}</h1>
              {user?.user_id && meeting.host_id === user.user_id && (
                <button
                  onClick={() => { setTitleDraft(meeting.title || ""); setEditingTitle(true); }}
                  className="text-xs text-[#005e7a] font-medium hover:underline ml-2"
                  data-testid="lobby-rename-button"
                >
                  Rename
                </button>
              )}
            </div>
          )}
          <p className="text-sm text-[#82828c] mb-3">Hosted by {meeting.host_name}</p>
          <p className="text-sm text-[#4a4a52] mb-6">
            {formatInTz(meeting.start_time, user?.timezone || userBrowserTimezone())} · {meeting.duration_minutes}m
          </p>

          <div className="flex flex-col gap-3 max-w-md">
            <div>
              <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">
                Your display name
              </label>
              <input
                className="bloq-input mt-1"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="How should we show your name?"
                data-testid="lobby-display-name-input"
              />
            </div>
            {cams.length > 1 && (
              <div>
                <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Camera</label>
                <Select value={camId} onValueChange={switchCam}>
                  <SelectTrigger className="mt-1" data-testid="lobby-camera-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {cams.map((c) => <SelectItem key={c.deviceId} value={c.deviceId}>{c.label || "Camera"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {mics.length > 1 && (
              <div>
                <label className="text-xs uppercase tracking-widest text-[#82828c] font-medium">Microphone</label>
                <Select value={micId} onValueChange={switchMic}>
                  <SelectTrigger className="mt-1" data-testid="lobby-mic-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {mics.map((m) => <SelectItem key={m.deviceId} value={m.deviceId}>{m.label || "Microphone"}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <button onClick={handleJoin} disabled={isJoining} className="bloq-btn-primary mt-3 disabled:opacity-60" data-testid="lobby-join-button">
              {isJoining ? "Joining…" : "Join meeting"}
            </button>
            {permError && (
              <p className="text-sm text-red-600">{permError}</p>
            )}
          </div>
        </div>

        <div className="bloq-meeting-tile relative" style={{ background: "#1a1a22" }} data-testid="lobby-preview">
          {FLAG ? (
            <div ref={lobbyPreviewRef} className="w-full h-full" data-testid="lobby-video-preview" />
          ) : (
            <video ref={videoRef} autoPlay muted playsInline />
          )}
          {!camOn && (
            <div className="absolute inset-0 flex items-center justify-center text-white/70">
              <div className="text-center">
                <Camera className="w-10 h-10 mx-auto opacity-50" />
                <p className="text-sm mt-2">Camera off</p>
              </div>
            </div>
          )}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bloq-meeting-controls">
            <button onClick={() => setMicOn((v) => !v)} className="bloq-icon-btn" data-active={String(micOn)} data-testid="lobby-toggle-mic">
              {micOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
            </button>
            <button onClick={() => setCamOn((v) => !v)} className="bloq-icon-btn" data-active={String(camOn)} data-testid="lobby-toggle-cam">
              {camOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
            </button>
            {FLAG && effectsSupported && (
              <button
              onClick={() => setEffectsOpen((v) => !v)}
              className="bloq-icon-btn"
              data-fx={(effectsOpen || (videoEffects.background && videoEffects.background !== "none") || videoEffects.beautyEnabled) ? "on" : "off"}
              title="Video effects"
              data-testid="lobby-video-effects-button"
              >
                <Wand2 className="w-5 h-5" />
              </button>
            )}
          </div>
          {FLAG && effectsOpen && effectsSupported && (
            <VideoEffectsPanel
              settings={videoEffects}
              onChange={updateVideoEffects}
              onClose={() => setEffectsOpen(false)}
              ids={LOBBY_EFFECT_IDS}
              caps={caps}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function CenteredLoader() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#fbfcfc]">
      <BloqLogo size={26} />
      <div className="bloq-spinner mt-6" />
    </div>
  );
}

function CenteredCard({ title, children }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#fbfcfc]">
      <div className="bloq-card p-8 max-w-md flex flex-col gap-2">
        <BloqLogo size={24} />
        <p className="bloq-headline text-2xl mt-2">{title}</p>
        {children}
      </div>
    </div>
  );
}
